import { defineStore } from "pinia";
import { computed, toRefs, reactive } from "vue";
import type { IDomainDiscount } from "~/domain/discount";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useBugReporter, useDirectBackend } from "#imports";

// Define the state interface
type IState = {
  isLoaded: boolean;
  data: IDomainDiscount[];
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    data: [],
  };
}

export const useDiscountsStore = defineStore("discounts", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  const { report } = useBugReporter();
  const { discountRepo } = useDirectBackend();

  async function load(
    businessId: string,
    outletId: string,
    orderingMode: IDomainOrderingMode,
  ) {
    try {
      const response = await discountRepo.getDiscounts({
        businessId,
        outletId,
        orderingMode,
      });

      reset();
      patch({
        isLoaded: true,
        data: response.data,
      });
    } catch (e) {
      // if by any means it fails
      // there is no reason to break the user xp
      // only report a bug
      report(e);
    }
  }

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const hasDiscounts = computed(() => state.data.length > 0);

  const getBestAutoDiscount = (orderingMode: IDomainOrderingMode) => {
    const discountsForMode = state.data.filter((discount) =>
      discount.orderingModes.includes(orderingMode),
    );

    return discountsForMode.reduce(
      (highest: IDomainDiscount | undefined, discount: IDomainDiscount) => {
        if (
          discount.autoCheckout &&
          (!highest || discount.amount > highest.amount)
        ) {
          return discount;
        }

        return highest;
      },
      undefined,
    );
  };

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    load,
    hasDiscounts,
    getBestAutoDiscount,
  };
});
